import { Link } from 'react-router-dom';
import MetaHead from '../../components/MetaHead';
import { imageUrl, srcSet } from '../../lib/images';
import { DanmarkMiniCallout } from '../../components/SygeforsikringDanmark';

export default function SamtaleterapiPage() {
  const symptomGroups = [
    {
      title: 'Trivsel og belastning',
      items: ['Stress og udbrændthed', 'Angst og tankemylder', 'Depression', 'Belastningsreaktioner', 'Lavt selvværd', 'Humørsvingninger']
    },
    {
      title: 'Livskriser og sorg',
      items: ['Kriser og midtlivskrise', 'Sorg og tab', 'Sygdomme og kræft', 'Smerteproblematikker', 'Pårørende', 'Ulykker']
    },
    {
      title: 'Relationer og familie',
      items: ['Parforholdsproblemer', 'Skilsmisse', 'Sammenbragte familier', 'Konflikter og temperament', 'Omsorgstræthed']
    },
    {
      title: 'Krop, køn og traumer',
      items: ['Kropsbillede / krop', 'Fødselsreaktioner og abort', 'Kønsidentitet og kønsubehag', 'Vold og overgreb', 'Traumatiske oplevelser']
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      <MetaHead 
        title="Samtaleterapi - Din Privat Psykolog"
        description="Samtaleterapi i klinikken i Gram samt online. Individuelle terapeutiske samtaler tilpasset din problemstilling og livssituation ved autoriseret psykolog Marie Aagaard Larsen."
        ogTitle="Samtaleterapi - Din Privat Psykolog"
        ogDescription="Samtaleterapi i klinikken i Gram samt online. Individuelle terapeutiske samtaler tilpasset din problemstilling og livssituation ved autoriseret psykolog Marie Aagaard Larsen."
        ogImage="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/image_005daf1a.png"
      />

      {/* Breadcrumb / Back Link */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-[#465A4F]">
        <Link to="/" className="hover:text-[#5f7365] underline font-medium">
          Forside
        </Link>
        <span>/</span>
        <span className="text-[#5f7365] font-semibold">Samtaleterapi</span>
      </div>

      {/* Header */}
      <div className="border-b border-[#E0D7C6] pb-8 space-y-4">
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#5f7365]">
          Samtaleterapi
        </h1>
        <p className="text-base sm:text-lg text-[#3B4D42] max-w-3xl leading-relaxed">
          Individuelle og nærværende terapeutiske samtaler tilpasset din konkrete problemstilling, livssituation og personlige ressourcer.
        </p>
      </div>

      {/* Main Content & Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6">
          {/* Clinic Photo Banner */}
          <div className="overflow-hidden border border-[#EDE6D8]">
            <img
              src={imageUrl('image_005daf1a.png', 'width=1200,quality=90,format=auto')}
              srcSet={srcSet('image_005daf1a.png', [600, 900, 1200, 1600])}
              sizes="(min-width: 1280px) 800px, (min-width: 1024px) 66vw, 100vw"
              alt="Rolige og trygge rammer til samtaleterapi i klinikken"
              className="w-full h-64 sm:h-80 object-cover object-bottom"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="space-y-4 text-sm sm:text-base text-[#5f7365] leading-relaxed">
            <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#5f7365]">
              Trygge og professionelle rammer for samtalen
            </h2>
            <p>
              I samtaleterapien arbejder vi ud fra dine ønsker, udfordringer og behov. Jeg har over 20 års bred klinisk erfaring inden for behandlingsområdet, kriser og traumer. Samtalerne foregår i rolige og trygge rammer i min privatklinik i Gram eller online.
            </p>
            <p>
              Jeg gør brug af en række psykologfaglige metoder – herunder kognitiv terapi, ACT, mentaliseringsbaseret terapi og EMDR – og tilpasser altid forløbet til dig som det hele menneske.
            </p>

            <h3 className="font-serif text-lg font-bold text-[#5f7365] pt-2">
              Hvad kan vi arbejde med i samtaleterapien?
            </h3>
            <p>
              Samtaleterapi er velegnet til både akutte kriser, belastninger, livsovergange samt mere langvarige problemstillinger såsom angst, depression, stress, lavt selvværd, sorgen over tab eller konflikter i relationer og parforhold.
            </p>

            <div className="pt-2 space-y-2">
              <h4 className="font-serif text-base font-bold text-[#5f7365] flex items-center gap-2">
                <i className="fa-regular fa-location-dot text-[#5f7365]" aria-hidden="true" />
                Praktisk information og adresse
              </h4>
              <p className="text-xs sm:text-sm text-[#465A4F] leading-relaxed">
                Samtalerne finder sted i klinikken på Torvegade 8, 1. sal, 6510 Gram. En individuel session varer 45 minutter.
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-[#EDE6D8] flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/kontakt"
              className="px-6 py-3.5 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <i className="fa-regular fa-calendar-check" aria-hidden="true" />
              <span>Bestil tid til samtaleterapi</span>
            </Link>
            <Link
              to="/priser"
              className="px-5 py-3 bg-white hover:bg-white text-[#5f7365] font-semibold rounded-none border border-[#5f7365] text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <span>Se priser og tilskud</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-[#5f7365] text-white rounded-none p-6 space-y-4">
            <div className="space-y-1">
              <h3 className="font-serif text-lg font-bold text-white">
                Henvendelsesårsager
              </h3>
              <p className="text-xs text-[#C8D6CE]">
                Oplever du nogle af følgende symptomer eller livsomstændigheder?
              </p>
            </div>

            <div className="space-y-3.5 pt-1">
              {symptomGroups.map((group) => (
                <div key={group.title} className="bg-[#4e6053]/80 border border-[#3d4c42]/60 p-3.5 space-y-2">
                  <h4 className="text-xs font-semibold text-[#D5E3DB] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A3BEAF]" />
                    <span>{group.title}</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1 text-xs text-[#EAF0EC]">
                    {group.items.map((item) => (
                      <div key={item} className="flex items-center gap-1.5 leading-snug">
                        <span className="w-1 h-1 rounded-full bg-[#A3BEAF]/60 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-white/10 space-y-3">
              <p className="text-xs text-[#C8D6CE]">
                Er du i tvivl om samtaleterapi er det rette for dig? Ring gerne for en uforpligtende afklaring.
              </p>
              <Link
                to="/kontakt"
                className="w-full py-2.5 px-4 bg-white text-[#5f7365] font-semibold rounded-none text-xs flex items-center justify-center gap-2 hover:bg-[#FAF8F3] transition-all duration-200 hover:-translate-y-1"
              >
                <i className="fa-regular fa-envelope" aria-hidden="true" />
                Kontakt Din Privat Psykolog
              </Link>
            </div>
          </div>

          <DanmarkMiniCallout className="shadow-2xs" />
        </div>
      </div>
    </div>
  );
}
